<template>
	<!-- <div class="relative inline-block"> -->

		<!-- Teleported mode keeps a zero-size marker behind so the panel, now a child of
		     <body>, can still be positioned against the trigger it belongs to. Absolute +
		     zero-size: it never takes part in the host's layout. -->
		<span v-if="teleport" :ref="`dropdownanchor-${id}`" class="absolute w-0 h-0" aria-hidden="true"></span>

		<Teleport to="body" :disabled="!teleport">
		<Transition
            enter-from-class="transform opacity-0 scale-95"
            enter-active-class="transition ease-out duration-100"
            enter-to-class="transform opacity-100 scale-100"
            leave-from-class="transform opacity-100 scale-100"
            leave-active-class="transition ease-in duration-75"
            leave-to-class="transform opacity-0 scale-95"
        >
			<div v-show="show" v-bind="$attrs" :id="id" @focusout="handleFocusOut" :ref="`dropdowncontent-${id}`" tabindex="-1" class="z-50 focus:outline-none" :class="positioningClasses" :style="floatingStyle">
				<slot :open="open" :close="close"></slot>
			</div>
		</Transition>
		</Teleport>

    <!-- </div> -->
</template>


<script lang="ts">
// Rather simple popover implementation based on focus/focus-out https://stackoverflow.com/a/53956904
export default {
	inheritAttrs: false,
	emits: [
		'open',
		'close',
		'clickOutside',
	],
	props: {
		position: {
			type: String,
			default: 'bottom-end',
			validator(value) {
                return ['bottom-end', 'bottom-start', 'top-end', 'top-start'].includes(value);
            },
		},
		showOnMount: {
			type: Boolean,
			default: false,
		},
		// Render the panel in <body> instead of in place, positioned against the trigger.
		// Needed inside scroll containers that clip their overflow (the drawer's <main> is
		// overflow-x-hidden, which cut off any panel wider than the space beside its button).
		teleport: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			show: false,
			id: 'dd-' + Math.floor(Math.random() * 999999),
			floatingStyle: null as Record<string, string> | null,
			// Listener kept while open so the panel follows its trigger; not a `_`-prefixed
			// instance field, which Vue does not expose on the public proxy.
			triggerTracker: null as (() => void) | null,
		};
	},
	computed: {
		positioningClasses() {
			// Teleported panels are positioned from measurements (see updateFloatingPosition);
			// only the transform origin, which drives the open/close scale, still applies.
			const origins = {
				'bottom-start': 'origin-top-left',
				'top-start': 'origin-bottom-left',
				'top-end': 'origin-bottom-right',
				'bottom-end': 'origin-top-right',
			};
			const origin = origins[this.position] ?? origins['bottom-end'];

			if (this.teleport) {
				return 'fixed ' + origin;
			}

			switch(this.position) {
				case 'bottom-start':
					return 'absolute left-1 mt-2 ' + origin;
				case 'top-start':
					return 'absolute left-1 bottom-full mb-2 ' + origin;
				case 'top-end':
					return 'absolute right-0 bottom-full mb-2 ' + origin;
				default:
					return 'absolute right-0 mt-2 ' + origin;
			}
		},
	},
	mounted() {
		if(this.showOnMount) {
			this.open();
		}
	},
	beforeUnmount() {
		this.stopTrackingTrigger();
	},
	methods: {
		open() {
			this.show = true;
			this.$emit('open');
			this.$nextTick(() => this.updateFloatingPosition());
			this.startTrackingTrigger();
			setTimeout(() => this.$refs[`dropdowncontent-${this.id}`].focus(), 100);	// add short timeout to allow popover to be rendered properly
		},
		close() {
			this.show = false;
			this.stopTrackingTrigger();
			this.$emit('close');
		},

		/**
		 * Place the teleported panel next to its trigger, clamped to the viewport so it can
		 * never end up (partly) off-screen. The marker's parent is the trigger wrapper.
		 */
		updateFloatingPosition() {
			if (!this.teleport) {
				return;
			}

			const panel = this.$refs[`dropdowncontent-${this.id}`] as HTMLElement | undefined;
			const trigger = (this.$refs[`dropdownanchor-${this.id}`] as HTMLElement | undefined)?.parentElement;
			if (!panel || !trigger) {
				return;
			}

			const rect = trigger.getBoundingClientRect();
			const gap = 8;
			const margin = 8;
			const width = panel.offsetWidth;
			const height = panel.offsetHeight;

			const maxLeft = Math.max(margin, window.innerWidth - width - margin);
			const left = this.position.endsWith('start') ? rect.left : rect.right - width;

			const maxTop = Math.max(margin, window.innerHeight - height - margin);
			const top = this.position.startsWith('top') ? rect.top - height - gap : rect.bottom + gap;

			this.floatingStyle = {
				left: Math.min(Math.max(margin, left), maxLeft) + 'px',
				top: Math.min(Math.max(margin, top), maxTop) + 'px',
			};
		},

		startTrackingTrigger() {
			if (!this.teleport || this.triggerTracker) {
				return;
			}
			this.triggerTracker = () => this.updateFloatingPosition();
			// Capture: the trigger usually lives in a nested scroll container, whose scroll
			// events do not bubble to window.
			window.addEventListener('scroll', this.triggerTracker, true);
			window.addEventListener('resize', this.triggerTracker);
		},

		stopTrackingTrigger() {
			if (!this.triggerTracker) {
				return;
			}
			window.removeEventListener('scroll', this.triggerTracker, true);
			window.removeEventListener('resize', this.triggerTracker);
			this.triggerTracker = null;
		},
		// focusout, not blur: blur does not bubble, so a panel whose focus sits on a child
		// (e.g. a goal row's checkbox) would never see it and the panel could not close.
		handleFocusOut(event) {
			if(!event.relatedTarget || !event.relatedTarget.closest('#' + this.id)) {	// only close when focus is lost to outside element
				this.close();
				this.$emit('clickOutside');
			}
		},
	}
}
</script>
